import React from 'react'
import { PlusIcon, XIcon } from 'lucide-react'
import {
  ACCESS_TYPES,
  BUILDING_TYPES,
  type AdditionalAccess,
  type PropertyInfoData,
} from './types'

interface PropertyInfoFieldsProps {
  value: PropertyInfoData
  onChange: (value: PropertyInfoData) => void
  additionalAccess: AdditionalAccess[]
  onAdditionalAccessChange: (value: AdditionalAccess[]) => void
  compact?: boolean
}

export function PropertyInfoFields({
  value,
  onChange,
  additionalAccess,
  onAdditionalAccessChange,
  compact,
}: PropertyInfoFieldsProps) {
  const inputClass = compact
    ? 'w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm'
    : 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'

  const set = (patch: Partial<PropertyInfoData>) => onChange({ ...value, ...patch })

  const updateAdditionalAccess = (id: string, field: string, val: string) => {
    onAdditionalAccessChange(
      additionalAccess.map((a) => (a.id === id ? { ...a, [field]: val } : a))
    )
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Building Type</label>
          <select
            value={value.building_type}
            onChange={(e) => set({ building_type: e.target.value })}
            className={inputClass}
          >
            <option value="">Select type</option>
            {BUILDING_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Units</label>
          <input
            type="number"
            value={value.units}
            onChange={(e) => set({ units: e.target.value })}
            placeholder="e.g., 10"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Stories</label>
          <input
            type="number"
            value={value.stories}
            onChange={(e) => set({ stories: e.target.value })}
            placeholder="e.g., 3"
            className={inputClass}
          />
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Access Information</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Access Type</label>
          <select
            value={value.access_type}
            onChange={(e) => set({ access_type: e.target.value })}
            className={inputClass}
          >
            <option value="">Select access type</option>
            {ACCESS_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Access Info</label>
          <input
            type="text"
            value={value.access_info}
            onChange={(e) => set({ access_info: e.target.value })}
            placeholder="e.g., Code: 1234 or Location: Under mat"
            className={inputClass}
          />
        </div>

        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-900">Additional Access</h4>
          {additionalAccess.map((access) => (
            <div key={access.id} className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <input
                type="text"
                value={access.location}
                onChange={(e) => updateAdditionalAccess(access.id, 'location', e.target.value)}
                className={inputClass}
                placeholder="Location (e.g., Basement)"
              />
              <select
                value={access.access_type}
                onChange={(e) => updateAdditionalAccess(access.id, 'access_type', e.target.value)}
                className={inputClass}
              >
                <option value="">Select type</option>
                {ACCESS_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={access.access_info}
                onChange={(e) => updateAdditionalAccess(access.id, 'access_info', e.target.value)}
                className={inputClass}
                placeholder="Access info"
              />
              <button
                type="button"
                onClick={() =>
                  onAdditionalAccessChange(additionalAccess.filter((a) => a.id !== access.id))
                }
                className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              onAdditionalAccessChange([
                ...additionalAccess,
                {
                  id: Date.now().toString(),
                  location: '',
                  access_type: '',
                  access_info: '',
                },
              ])
            }
            className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            Add Additional Access
          </button>
        </div>
      </div>
    </div>
  )
}
